import { Button, CssBaseline, MuiThemeProvider, Snackbar } from '@material-ui/core';
import blue from '@material-ui/core/colors/blue';
import { createMuiTheme } from '@material-ui/core/styles';
import * as firebase from 'firebase/app';
import { buildClientSchema, getOperationAST, introspectionQuery, parse, visit } from 'graphql';
import React, { PureComponent } from 'react';
import 'typeface-roboto'
import Header from './Header';
import QueryTab from './QueryTab';
import SchemaTab from './SchemaTab';

const theme = createMuiTheme({
    palette: {
        primary: blue,
        type: 'dark'
    },
    overrides: {
        MuiInput: {
            root: {
                borderRadius: '4px',
                overflow: 'hidden',
                background: 'rgba(255, 255, 255, 0.06)',
                '&:hover': {
                    background: 'rgba(255, 255, 255, 0.12)',
                }
            },
            underline: {
                '&:before': {
                    height: '2px'
                }
            },
            input: {
                padding: '8px 8px 8px 8px'
            }
        },
        MuiLinearProgress: {
            colorPrimary: {
                backgroundColor: 'rgba(33, 150, 243, 0.5)'
            }
        }
    }
});

let requestIdCounter = 0;

class App extends PureComponent {
    constructor(props, context) {
        super(props, context);
        const query = window.localStorage.getItem('query') || `#
# 👋 Hi!
#
# To get started, use a saved query from the menu on the left or modify the one below.
# Don't forget to select the environment before hitting send.
#
query WhoAmI {
  viewer {
    displayName
  }
}`;
        const {
            queryValid,
            operationName,
            operationType
        } = this._parseQuery(query);
        this.state = {
            tab: 0,
            environments: {
                local: {
                    url: 'http://127.0.0.1:9000/api',
                    app: 'staging'
                },
                staging: {
                    url: 'https://hirer-staging.picker.work/api',
                    app: 'staging'
                },
                live: {
                    url: 'https://hirer.picker.work/api',
                    app: 'live'
                }
            },
            apps: {
                staging: {
                    auth: firebase.app('staging').auth()
                },
                live: {
                    auth: firebase.app('live').auth()
                }
            },
            auth: firebase.app('devops').auth(),
            environment: window.localStorage.getItem('environment') || 'staging',
            savedQueries: [],
            fetchingSchema: true,
            query,
            queryValid,
            operationName,
            operationType
        };
    }

    componentDidMount() {
        this._authSubscribers = [
            this.state.apps.staging.auth.onAuthStateChanged((currentUser) => {
                this.setState({
                    apps: Object.assign({}, this.state.apps, {
                        staging: Object.assign({}, this.state.apps.staging, {
                            user: currentUser
                        })
                    })
                });
            }),
            this.state.apps.live.auth.onAuthStateChanged((currentUser) => {
                this.setState({
                    apps: Object.assign({}, this.state.apps, {
                        live: Object.assign({}, this.state.apps.live, {
                            user: currentUser
                        })
                    })
                });
            }),
            this.state.auth.onAuthStateChanged((currentUser) => {
                this.setState({
                    user: currentUser
                });
                if (currentUser) {
                    const db = firebase.app('devops').firestore();
                    db.collection("queries").where('deleted', '==', false).onSnapshot((snapshot) => {
                        const docs = [];
                        snapshot.forEach(d => {
                            docs.push(d.data());
                        });
                        this.setState({
                            savedQueries: docs
                        });
                    });
                }
            })
        ];
        this._loadSchema();
    }

    componentWillUnmount() {
        if (this._authSubscribers) {
            this._authSubscribers.forEach(f => f());
        }
    }

    componentDidUpdate(prevProps, prevState) {
        if (this.state.environment !== prevState.environment) {
            this._loadSchema();
        }
    }

    render() {
        const environment = this._getEnvironment();
        const filteredQueries = this.state.queryFilter ? this.state.savedQueries.filter(q => q.name.toLowerCase().includes(this.state.queryFilter.toLowerCase())) : this.state.savedQueries;

        return (
            <MuiThemeProvider theme={theme}>
                <CssBaseline />
                <div className="app">
                    <Header
                        tab={this.state.tab}
                        environment={this.state.environment}
                        fetchingSchema={this.state.fetchingSchema}
                        errorFetchingSchema={this.state.errorFetchingSchema}
                        onChangeTab={this._handleChangeTab}
                        onChangeEnvironment={this._handleEnvironmentSelected}
                        onRefetchSchema={this._loadSchema} />
                    {this.state.tab === 0 && <QueryTab
                        user={this.state.user}
                        environmentKey={this.state.environment}
                        environment={environment}
                        query={this.state.query}
                        response={this.state.response}
                        savedQueries={filteredQueries}
                        queryValid={this.state.queryValid}
                        queryFilter={this.state.queryFilter}
                        onLogin={this._handleLogin}
                        onEnvironmentLogin={this._handleEnvironmentLogin}
                        onSaveQuery={this._handleSaveQuery}
                        onSelectQuery={this._handleSelectQuery}
                        onDeleteQuery={this._handleDeleteQuery}
                        onChangeQueryFilter={this._handleChangeQueryFilter}
                        onChangeQuery={this._handleChangeQuery}
                        onExecuteRequest={this._runQuery} />}
                    {this.state.tab === 1 && <SchemaTab
                        environment={environment}
                        fetchingSchema={this.state.fetchingSchema}
                        errorFetchingSchema={this.state.errorFetchingSchema}
                        onRefetchSchema={this._loadSchema} />}
                </div>
                <Snackbar
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left'
                    }}
                    open={this.state.deletedQueryName != null}
                    autoHideDuration={6000}
                    onClose={this._handleDeleteQuerySnackbarClose}
                    message={<span>Query deleted</span>}
                    action={<Button color="secondary" size="small" onClick={this._handleUndoDeleteQuery}>UNDO</Button>}/>
            </MuiThemeProvider>
        );
    }

    _handleChangeTab = (e, value) => {
        this.setState({
            tab: value
        });
    };

    _handleEnvironmentSelected = (e) => {
        const environmentKey = e.target.value;
        window.localStorage.setItem('environment', environmentKey);
        this.setState({
            environment: environmentKey,
            queryResponse: null,
            queryError: null
        });
    };

    _handleEnvironmentLogin = () => {
        const provider = new firebase.auth.GoogleAuthProvider();
        return this._getEnvironment().app.auth.signInWithPopup(provider);
    };

    _handleLogin = () => {
        const provider = new firebase.auth.GoogleAuthProvider();
        return this.state.auth.signInWithPopup(provider);
    };

    _graphQLFetcher = async (graphQLParams, withToken = true) => {
        const env = this._getEnvironment();
        const token = withToken ? ('limber-api-v3 ' + await env.app.user.getIdToken()) : null;
        const response = await fetch(env.url + '/graphql', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': token
            },
            body: JSON.stringify(graphQLParams),
        });
        return await response.json();
    };

    _loadSchema = () => {
        this.setState({
            fetchingSchema: true,
            errorFetchingSchema: false
        });
        this._graphQLFetcher({
            query: introspectionQuery,
            operationName: 'IntrospectionQuery'
        }, false).then((result) => {
            if (result && result.data) {
                const schema = buildClientSchema(result.data);
                this.setState({
                    fetchingSchema: false,
                    environments: Object.assign({}, this.state.environments, {
                        [this.state.environment]: Object.assign({}, this.state.environments[this.state.environment], {
                            schema,
                            response: result
                        })
                    })
                });
            }
        }, () => {
            this.setState({
                fetchingSchema: false,
                errorFetchingSchema: true
            });
        });
    };

    _getEnvironment = () => {
        const env = this.state.environments[this.state.environment];
        return Object.assign({}, env, {
            app: this.state.apps[env.app]
        });
    };

    _handleChangeQueryFilter = (filter) => {
        this.setState({
            queryFilter: filter
        });
    };

    _handleChangeQuery = (query) => {
        window.localStorage.setItem('query', query);
        const {
            queryValid,
            operationName,
            operationType
        } = this._parseQuery(query);
        this.setState({
            query,
            queryValid,
            operationName,
            operationType
        });
    };

    _parseQuery = (query) => {
        try {
            const queryDocument = parse(query);
            const operations = getOperations(queryDocument);
            const { name: { value: operationName }, operation: operationType } = getOperationAST(queryDocument);
            return {
                queryValid: operations.length === 1,
                operationName: operationName,
                operationType: operationType
            };
        } catch (e) {
            return {
                queryValid: false
            };
        }
    };

    _handleSaveQuery = () => {
        const db = firebase.app('devops').firestore();
        db.collection('queries').doc(this.state.operationName).set({
            name: this.state.operationName,
            query: this.state.query,
            type: this.state.operationType.toUpperCase(),
            deleted: false
        });
    };

    _handleSelectQuery = (query) => {
        this.setState({
            query: query.query
        });
    };

    _handleDeleteQuery = (query) => {
        const db = firebase.app('devops').firestore();
        db.collection('queries').doc(query.name).update({
            deleted: true
        });
        this.setState({
            deletedQueryName: query.name
        });
    };

    _handleUndoDeleteQuery = () => {
        if (!this.state.deletedQueryName) {
            return;
        }

        const db = firebase.app('devops').firestore();
        db.collection('queries').doc(this.state.deletedQueryName).update({
            deleted: false
        });
        this._handleDeleteQuerySnackbarClose();
    };

    _handleDeleteQuerySnackbarClose = () => {
        this.setState({
            deletedQueryName: null
        })
    };

    _runQuery = () => {
        if (!this.state.queryValid) {
            return;
        }

        const startTime = window.performance.now();
        const requestId = requestIdCounter++;

        this.setState({
            requestId,
            response: {
                fetching: true,
                startTime,
                body: (this.state.response || {}).body
            }
        });
        this._graphQLFetcher({
            query: this.state.query,
            operationName: this.state.operationName
        }).then((result) => {
            if (this.state.requestId === requestId) {
                this.setState({
                    response: {
                        fetching: false,
                        startTime,
                        endTime: window.performance.now(),
                        body: JSON.stringify(result, null, 2),
                        errors: result.errors
                    }
                });
            }
        }, (error) => {
            if (this.state.requestId === requestId) {
                this.setState({
                    response: {
                        fetching: false,
                        startTime,
                        endTime: window.performance.now(),
                        fetchError: error.message
                    }
                });
            }
        });
    };
}

function getOperations(ast) {
    const operationNodes = [];
    visit(ast, {
        OperationDefinition(node) {
            operationNodes.push(node);
        }
    });
    return operationNodes;
}

export default App;



// WEBPACK FOOTER //
// ./src/components/App.js